
import React from 'react';
import { Eye, EyeOff, UserPlus, Mail, Key } from 'lucide-react';
import { TabsContent } from '@/components/ui/tabs';
import { CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from './FormField';
import { AuthButton } from './AuthButton';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';

interface SignUpTabProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  showPassword: boolean;
  handlePasswordVisibility: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  success: string | null;
  setSuccess: (success: string | null) => void;
  resetErrors: () => void;
  setActiveTab: (tab: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const SignUpTab = ({
  email,
  setEmail,
  password,
  setPassword,
  showPassword,
  handlePasswordVisibility,
  loading,
  setLoading,
  success,
  setSuccess,
  resetErrors,
  setActiveTab,
  onKeyDown
}: SignUpTabProps) => {
  const { signUp } = useAuth();
  const { toast } = useToast();

  const handleSignUp = async () => {
    if (!email || !password) {
      toast({
        title: 'Erreur',
        description: 'Veuillez remplir tous les champs',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    resetErrors();
    
    try {
      const result = await signUp(email, password);
      
      if (result.error) {
        if (result.error.message?.includes('connexion réseau') || 
            result.error.message === 'Failed to fetch') {
          toast({
            title: 'Erreur de connexion',
            description: 'Problème de connexion internet. Veuillez réessayer.',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Erreur',
            description: result.error.message || 'Une erreur est survenue',
            variant: 'destructive',
          });
        }
      } else {
        setSuccess('Compte créé avec succès ! Vérifiez votre email pour confirmer votre compte.');
        toast({
          title: 'Compte créé',
          description: 'Vérifiez votre email pour confirmer votre compte',
        });
        setActiveTab('signin');
      }
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Une erreur est survenue',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <TabsContent value="signup" className="animate-fade-in">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl font-bold text-gray-800 dark:text-white">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-youtube-red to-youtube-red-dark">Créer un compte</span>
        </CardTitle>
        <CardDescription className="text-gray-500 dark:text-gray-400">Rejoignez PersonalTube dès aujourd'hui</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <FormField
          id="signup-email"
          type="email"
          placeholder="email@exemple.fr"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={onKeyDown}
          icon={<Mail className="w-4 h-4 mr-2 text-youtube-red-light" />}
          label="Email"
        />
        <FormField
          id="signup-password"
          type={showPassword ? 'text' : 'password'}
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={onKeyDown}
          icon={<Key className="w-4 h-4 mr-2 text-youtube-red-light" />}
          label="Mot de passe"
          rightIcon={showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          onClick={handlePasswordVisibility}
        />
        {success && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-md text-sm text-green-800 dark:text-green-400 animate-fade-in">
            {success}
          </div>
        )}
      </CardContent>
      <CardFooter className="pb-6">
        <AuthButton
          id="signup-button"
          onClick={handleSignUp}
          loading={loading}
          icon={<UserPlus className="w-5 h-5" />}
          text="S'inscrire"
          loadingText="Inscription..."
        />
      </CardFooter>
    </TabsContent>
  );
};
